import type { ContributorNameComponents, NewContributor } from '@ilteducation/content-ref-data-types';

/**
 * The name components in reading order. Contributors have no single sortable
 * property, the backend derives their sortKey from these - in this same order.
 */
export const NAME_COMPONENTS = ['title', 'firstName', 'middleName', 'lastName', 'suffix'] as const;

/**
 * Organizations have a single name, which we keep in `lastName` so that they
 * sort alongside people.
 */
export const ORGANIZATION_NAME_COMPONENT = 'lastName';

const trimmed = (value?: string) => value?.trim() ?? '';

/** Every component in reading order, blanks skipped - the contributor's full name. */
export const fullNameFromComponents = (name: Partial<ContributorNameComponents>): string =>
  NAME_COMPONENTS.map((component) => trimmed(name[component]))
    .filter(Boolean)
    .join(' ');

/**
 * The full name in reading order, which is what the backend's own
 * `contributorSortKey` builds - hence the same `Partial<NewContributor>`
 * argument, so both a saved contributor and a form draft can be passed.
 */
export const contributorFullName = (contributor?: Partial<NewContributor>): string =>
  contributor?.name ? fullNameFromComponents(contributor.name) : '';

/** Only the filled components, trimmed, so blanks are never sent or compared. */
export const nameComponentsOf = (source: Partial<ContributorNameComponents>): ContributorNameComponents =>
  NAME_COMPONENTS.reduce(
    (name, component) => (trimmed(source[component]) ? { ...name, [component]: trimmed(source[component]) } : name),
    {},
  );

type CreatorName = Partial<ContributorNameComponents> & { nameComponents?: ContributorNameComponents };

/**
 * The components of a book creator's name: `nameComponents` when the creator
 * carries them, otherwise the two-part name - all a book saved before
 * contributors existed has, where the components cannot be told apart.
 */
export const creatorNameComponents = (creator: CreatorName): ContributorNameComponents =>
  nameComponentsOf(creator.nameComponents ?? creator);

/**
 * content-registry stores a creator's name in two parts, so the components are
 * merged on the way into a book: title, first and middle name become
 * `firstName`, last name and suffix become `lastName`.
 */
export const personNameFromComponents = (source: Partial<ContributorNameComponents>) => ({
  firstName: [source.title, source.firstName, source.middleName].map(trimmed).filter(Boolean).join(' '),
  lastName: [source.lastName, source.suffix].map(trimmed).filter(Boolean).join(' '),
});
